/**
 * Dashboard Options Utility
 * Provides role-based dashboard options
 */

import { LayoutGrid, type LucideIcon, Scale, TrendingUp } from 'lucide-react';
import type { UserRole } from '@/types/roles';

export interface DashboardOption {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  route: string;
  isSpecialized?: boolean;
  requiredRoles?: UserRole[];
}

// All available dashboard options
export const allDashboardOptions: DashboardOption[] = [
  {
    id: 'main',
    title: 'Credit Control Dashboard',
    description: 'Overview of outstanding invoices and collection performance',
    icon: LayoutGrid,
    route: '/',
    isSpecialized: false,
    // Everyone can see this
  },
  {
    id: 'collections',
    title: 'Collections Dashboard',
    description: 'Collections queue and team performance metrics',
    icon: TrendingUp,
    route: '/collections/dashboard',
    isSpecialized: true,
    // Only managers and above can see this
    requiredRoles: ['manager', 'team-leader', 'fp-a', 'finance-director'],
  },
  {
    id: 'disputes',
    title: 'Disputes Dashboard',
    description: 'Dispute resolution tracking and analytics',
    icon: Scale,
    route: '/disputes',
    isSpecialized: true,
    // Everyone can see this
  },
];

/**
 * Get dashboard options based on user role and filter selection
 * @param userRole - The user's role
 * @param isEveryoneFilter - Whether "Everyone" filter is selected (defaults to true)
 */
export function getDashboardOptions(userRole: UserRole, isEveryoneFilter: boolean = true): DashboardOption[] {
  return allDashboardOptions.filter((option) => {
    // Collections Dashboard special rule: Only show when "Everyone" is selected
    if (option.id === 'collections') {
      // Must be manager or above AND "Everyone" filter must be selected
      const hasRole = option.requiredRoles?.includes(userRole) ?? false;
      return hasRole && isEveryoneFilter;
    }

    // For other dashboards, check role requirements only
    // If no required roles specified, everyone can see it
    if (!option.requiredRoles || option.requiredRoles.length === 0) {
      return true;
    }

    // Check if user's role is in the required roles
    return option.requiredRoles.includes(userRole);
  });
}

/**
 * Check if a user can access a specific dashboard
 */
export function canAccessDashboard(dashboardId: string, userRole: UserRole): boolean {
  const option = allDashboardOptions.find((opt) => opt.id === dashboardId);

  if (!option) {
    return false;
  }

  // If no required roles specified, everyone can access
  if (!option.requiredRoles || option.requiredRoles.length === 0) {
    return true;
  }

  // Check if user's role is in the required roles
  return option.requiredRoles.includes(userRole);
}

/**
 * Check if user is a manager or above
 */
export function isManagerOrAbove(userRole: UserRole): boolean {
  return ['manager', 'team-leader', 'fp-a', 'finance-director'].includes(userRole);
}
